import { forwardRef } from "react";
import { MDLINK_LOGO } from "@/lib/brand";
import {
    money,
    money0,
    unit,
    N,
    MODE_LABELS,
    selEquipos,
    equipoLista,
    equiposSubtotal,
    consLines,
    consAnnual,
    consAnnualCost,
    equiposCosto,
    equipoOtros,
    ivaRate,
    PMT,
    comodatoCalc,
    plazoDiscount,
} from "@/lib/unidosis";

/** Live quote panel — printable when the page is printed. */
const QuotePanel = forwardRef(function QuotePanel({ state }, ref) {
    const S = state;
    const P = S.settings.params;
    const equipos = selEquipos(S);
    const eqSub = equiposSubtotal(S);
    const cons = consLines(S);
    const consAnu = consAnnual(S);
    const iva = ivaRate(S);
    const isCom = S.mode === "comodato";
    const com = isCom ? comodatoCalc(S) : null;

    const fechaTxt = new Date().toLocaleDateString("es-CR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    // Equipment block by mode
    let oneTime = 0, monthly = 0, monthlyLabel = "", upfront = 0, upfrontLabel = "";
    let equipNote = "";
    let finInteres = 0, finFinanciado = 0, alqTotal = 0, alqMargenFin = 0;
    if (S.mode === "contado") {
        oneTime = eqSub * (1 - (S.contado.desc || 0) / 100);
        upfront = oneTime;
        upfrontLabel = "Total de contado";
        equipNote = S.contado.desc > 0
            ? `Incluye ${S.contado.desc}% de descuento de contado.`
            : "Pago único contra entrega e instalación.";
    } else if (S.mode === "financiamiento") {
        const eng = eqSub * (S.fin.enganche / 100);
        const fin = eqSub - eng;
        const cuota = PMT(fin, P.tasaFin, S.fin.plazo);
        upfront = eng;
        upfrontLabel = "Enganche";
        monthly = cuota;
        monthlyLabel = `Cuota × ${S.fin.plazo} meses`;
        finFinanciado = fin;
        finInteres = cuota * S.fin.plazo - fin;
        equipNote = `Financiamiento a ${S.fin.plazo} meses, tasa ${(P.tasaFin * 100).toFixed(1)}% anual. Total financiado ${money0(eng + cuota * S.fin.plazo)}.`;
    } else if (S.mode === "alquiler") {
        const meses = S.alq.anios * 12;
        const cuota = PMT(eqSub, P.tasaAlq, meses);
        monthly = cuota;
        monthlyLabel = `Canon × ${meses} meses`;
        alqTotal = cuota * meses;
        alqMargenFin = alqTotal - eqSub;
        equipNote = `Alquiler a ${S.alq.anios} año(s). El equipo es activo de MD Link; incluye mantenimiento preventivo.`;
    } else if (S.mode === "comodato") {
        equipNote = `Equipo entregado en comodato, sin costo de adquisición. Su valor más ${(com.tasa * 100).toFixed(1)}% de financiamiento se recupera dentro del precio de los consumibles, amortizado en ${com.meses} meses (${S.com.anios} año${S.com.anios > 1 ? "s" : ""}).`;
    }

    const capSub = upfront;
    const capIva = capSub * iva;
    const monIva = monthly * iva;
    const consMonthly = consAnu / 12;
    const consIvaAnnual = consAnu * iva;
    const plazoAnios = isCom ? (S.com.anios || 1) : { 1: 1, 2: 2, 3: 3, 4: 4 }[S.plazoCons];

    return (
        <div ref={ref} className="quote-print-area bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="quote-panel">
            {/* Header */}
            <div className="flex items-start gap-3 p-5 border-b-2 border-slate-900">
                <img
                    src={MDLINK_LOGO}
                    alt="MD Link"
                    className="h-9 w-auto flex-none mt-0.5"
                    draggable={false}
                />
                <div className="min-w-0 pl-3 border-l border-slate-200">
                    <h3 className="font-heading text-base font-semibold tracking-tight text-slate-900">
                        Cotización Opus Pac
                    </h3>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                        Soluciones de unidosis · Costa Rica
                    </div>
                </div>
                <div className="ml-auto text-right">
                    <span
                        className={
                            "inline-block text-[10px] font-mono uppercase tracking-widest font-bold px-2 py-1 rounded-sm " +
                            (S.tipo === "CCSS" ? "bg-blue-50 text-[#1F5FA8]" : "bg-emerald-50 text-emerald-700")
                        }
                        data-testid="quote-badge-tipo"
                    >
                        {S.tipo === "CCSS" ? "Institucional · CCSS" : "Privado"}
                    </span>
                    <div className="text-[11px] font-mono text-slate-500 mt-1" data-testid="quote-folio">
                        {S.folio || ""}
                    </div>
                </div>
            </div>

            <div className="px-5 pt-3 pb-4">
                {/* meta */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 py-3 border-b border-slate-200 text-[11.5px]">
                    <Row label="Cliente" value={S.cliente || "—"} />
                    <Row label="Fecha" value={fechaTxt} />
                    <Row label="Contacto" value={S.contacto || "—"} />
                    <Row label="Vigencia" value={`${S.vigencia || 30} días`} />
                    <Row label="Moneda" value="USD" />
                    <Row label="Modalidad" value={MODE_LABELS[S.mode]} />
                </div>

                {/* Equipos */}
                <Section title="Equipos">
                    {equipos.length ? (
                        equipos.map((e) => (
                            <QLine
                                key={e.id}
                                title={e.nombre}
                                sub={`${e.qty} × ${money0(equipoLista(S, e))}${e.tipo === "servicio" ? " · servicio" : ""}`}
                                value={
                                    isCom ? (
                                        <span className="text-[10px] uppercase font-mono text-[#1BA5D9] font-bold">
                                            Incluido en consumibles
                                        </span>
                                    ) : (
                                        money0(equipoLista(S, e) * e.qty)
                                    )
                                }
                                testId={`quote-eq-${e.id}`}
                            />
                        ))
                    ) : (
                        <div className="text-[12px] italic text-slate-400 py-1.5">Sin equipos seleccionados.</div>
                    )}
                    {equipos.some((e) => e.tipo !== "servicio") && (
                        <div className="text-[10px] text-slate-400 mt-1.5">
                            Precio incluye implementación, puesta en marcha, mantenimiento e imprevistos.
                        </div>
                    )}
                    {equipNote && (
                        <div className="text-[10.5px] text-slate-500 mt-2 leading-relaxed">{equipNote}</div>
                    )}
                </Section>

                {/* Consumibles */}
                <Section
                    title={`${isCom ? "Consumibles · equipo incluido" : "Consumibles"}${plazoAnios > 1 ? ` · contrato ${plazoAnios} años` : " · anual"}`}
                >
                    {isCom ? (
                        com.families.length ? (
                            com.families.map((fa) => (
                                <div key={fa.fam}>
                                    <div className="flex items-baseline justify-between gap-2 pt-2 mt-1 border-t border-dashed border-slate-200">
                                        <div className="min-w-0">
                                            <div className="text-[12.5px] font-semibold text-[#1BA5D9] font-heading">
                                                {fa.title}
                                            </div>
                                            <div className="text-[11px] text-slate-500">
                                                {fa.flatUnit != null
                                                    ? `${money(fa.flatUnit)} por consumible · ${N.format(fa.totalUnits)} u en ${com.years} año${com.years > 1 ? "s" : ""}`
                                                    : "sin volumen cargado"}
                                            </div>
                                        </div>
                                        <div className="text-right font-semibold text-[12.5px] whitespace-nowrap">
                                            {fa.flatUnit != null && (
                                                <>
                                                    {money0(fa.packageTotal)}
                                                    <div className="text-[10px] uppercase text-slate-400 font-mono">contrato</div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {fa.consLines.map((l) => (
                                        <QLine
                                            key={l.c.id}
                                            indent
                                            title={l.c.nombre}
                                            sub={`${N.format(l.vol)} u/año × ${fa.flatUnit != null ? money(fa.flatUnit) : "—"}`}
                                            value={fa.flatUnit != null ? money0(l.vol * fa.flatUnit) : "—"}
                                            valueSub="por año"
                                            testId={`quote-cons-${l.c.id}`}
                                        />
                                    ))}
                                </div>
                            ))
                        ) : (
                            <div className="text-[12px] italic text-slate-400 py-1.5">
                                Cargá volúmenes de consumibles para construir el comodato.
                            </div>
                        )
                    ) : cons.length ? (
                        cons.map((l) => {
                            const d = Math.round((1 - (1 - l.dv) * (1 - l.dp)) * 100);
                            return (
                                <QLine
                                    key={l.c.id}
                                    title={l.c.nombre}
                                    sub={`${N.format(l.vol)} u/año × ${unit(l.u)}${d > 0 ? ` · −${d}%` : ""}`}
                                    value={money0(l.annual)}
                                    valueSub="por año"
                                    testId={`quote-cons-${l.c.id}`}
                                />
                            );
                        })
                    ) : (
                        <div className="text-[12px] italic text-slate-400 py-1.5">Sin consumibles cargados.</div>
                    )}

                    {isCom ? (
                        com.families.length ? (
                            <div className="text-[10.5px] text-slate-500 mt-2 leading-relaxed">
                                Cada familia de equipo tiene su propio <b>precio único por consumible</b> (cualquier insumo X o Y de esa familia), que incorpora el equipo y el financiamiento repartidos entre sus unidades. El hospital adquiere los equipos a través del consumo, sin compra de activo.
                            </div>
                        ) : null
                    ) : cons.length ? (
                        <div className="text-[10.5px] text-slate-500 mt-2 leading-relaxed">
                            Precios sujetos a volumen mínimo anual; descuentos por volumen adicional y por plazo aplicados automáticamente.
                        </div>
                    ) : null}
                </Section>

                {/* Totals */}
                {isCom ? (
                    <div className="mt-4 pt-3 border-t border-slate-200">
                        <div className="bg-[#E1F4FB] rounded-md p-3 mt-1">
                            <div className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#1BA5D9] mb-1.5">
                                Precio por consumible · por familia
                            </div>
                            {com.families.length ? (
                                com.families.map((fa) => (
                                    <div key={fa.fam} className="flex justify-between text-[13px] py-0.5" data-testid={`quote-flatunit-${fa.fam}`}>
                                        <span>{fa.title}</span>
                                        <span className="font-mono font-bold text-[#1BA5D9]">
                                            {fa.flatUnit != null ? money(fa.flatUnit) + (iva > 0 ? " +IVA" : "") : "—"}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <div className="text-[12.5px] text-slate-500 italic">Sin volúmenes cargados</div>
                            )}
                            {iva > 0 && com.families.some((f) => f.flatUnit != null) && (
                                <div className="text-[11.5px] text-slate-500 mt-2">
                                    Con IVA 13%:{" "}
                                    {com.families
                                        .filter((f) => f.flatUnit != null)
                                        .map((f) => money(f.flatUnit * (1 + iva)))
                                        .join(" · ")}
                                </div>
                            )}
                        </div>

                        <div className="mt-3 space-y-1">
                            <TotalRow sub label="Plazo del comodato" value={`${com.years} año${com.years > 1 ? "s" : ""} (${com.meses} meses)`} />
                            <TotalRow sub label="Unidades del contrato" value={`${N.format(com.totalUnits)} u`} />
                            <TotalRow sub label="Valor equipos en comodato" value={money0(com.valor)} />
                            <TotalRow sub label="Total del contrato (sin IVA)" value={money0(com.packageTotal)} />
                            {iva > 0 && <TotalRow sub label="IVA 13%" value={money0(com.packageTotal * iva)} />}
                            <TotalRow grand label="Total del contrato" value={money0(com.packageTotal * (1 + iva))} testId="quote-grand-total" />
                            <TotalRow sub label={`Pago mensual estimado${iva > 0 ? " c-IVA" : ""}`} value={money(com.monthly * (1 + iva))} />
                        </div>
                        {com.totalUnits <= 0 && (
                            <div className="text-[11px] text-[#C0552B] mt-2">
                                ⚠ Cargá los volúmenes anuales de consumibles para calcular el precio por consumible.
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="mt-4 pt-3 border-t border-slate-200 space-y-1">
                            <TotalRow sub label={upfrontLabel || "Inversión en equipos"} value={money0(capSub)} />
                            {iva > 0 && <TotalRow sub label="IVA 13%" value={money0(capIva)} />}
                            <TotalRow grand label={S.mode === "contado" ? "Inversión total" : "Pago inicial"} value={money0(capSub + capIva)} testId="quote-grand-total" />
                        </div>

                        {monthly > 0 && (
                            <div className="bg-[#E1F4FB] rounded-md p-3 mt-3">
                                <div className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#1BA5D9] mb-1.5">
                                    {monthlyLabel}
                                </div>
                                <TotalRow sub label="Cuota mensual" value={money(monthly)} />
                                {iva > 0 && <TotalRow sub label="IVA 13%" value={money(monIva)} />}
                                <TotalRow grand label="Cuota mensual total" value={money(monthly + monIva)} testId="quote-monthly-total" />
                            </div>
                        )}

                        {consAnu > 0 && (
                            <div className="bg-amber-50 rounded-md p-3 mt-3">
                                <div className="text-[11px] font-mono font-bold tracking-widest uppercase text-amber-800 mb-1.5">
                                    Consumibles — gasto recurrente
                                </div>
                                <TotalRow sub label="Total anual" value={money0(consAnu)} />
                                <TotalRow sub label="Equivalente mensual" value={money0(consMonthly)} />
                                {iva > 0 && <TotalRow sub label="IVA 13% anual" value={money0(consIvaAnnual)} />}
                                <TotalRow grand label="Consumibles / año c-IVA" value={money0(consAnu + consIvaAnnual)} testId="quote-consumibles-annual" />
                                {plazoAnios > 1 && (
                                    <TotalRow sub label={`Compromiso ${plazoAnios} años (sin IVA)`} value={money0(consAnu * plazoAnios)} />
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Internal margin view */}
            {S.internal && <InternalPanel state={S} isCom={isCom} com={com} eqSub={eqSub} consAnu={consAnu} finInteres={finInteres} finFinanciado={finFinanciado} alqTotal={alqTotal} alqMargenFin={alqMargenFin} plazoAnios={plazoAnios} />}

            {/* Footer */}
            <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50 text-[10.5px] text-slate-500 leading-relaxed">
                <b className="text-slate-900">MD Link</b> · mdlink.tech · Distribuidor autorizado Opus Pac en Costa Rica.
                <br />
                Precios en dólares estadounidenses (USD), no incluyen{" "}
                {S.tipo === "CCSS"
                    ? "impuestos (exoneración institucional)"
                    : "otros tributos salvo el IVA indicado"}.
                Cotización válida por {S.vigencia || 30} días naturales a partir de la fecha de emisión.
                Sujeto a disponibilidad y a confirmación de condiciones de importación.
            </div>
        </div>
    );
});

function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-2">
            <span className="text-slate-500">{label}</span>
            <span className="font-medium text-right text-slate-900 font-mono truncate">{value}</span>
        </div>
    );
}
function Section({ title, children }) {
    return (
        <div className="mt-3">
            <div className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#1BA5D9] flex items-center gap-2 mb-1.5">
                {title}
                <span className="flex-1 h-px bg-slate-200" />
            </div>
            {children}
        </div>
    );
}
function QLine({ title, sub, value, valueSub, indent, testId }) {
    return (
        <div className="flex justify-between gap-2.5 py-1 items-baseline text-[12.5px]" data-testid={testId}>
            <div className={"flex-1 min-w-0 " + (indent ? "pl-3" : "")}>
                <div className="font-medium text-slate-900 truncate">{title}</div>
                {sub && <div className="text-slate-500 text-[11px] truncate">{sub}</div>}
            </div>
            <div className="font-semibold text-slate-900 text-right whitespace-nowrap font-mono">
                {value}
                {valueSub && <div className="text-[10.5px] uppercase text-slate-400 tracking-widest font-mono font-medium">{valueSub}</div>}
            </div>
        </div>
    );
}
function TotalRow({ label, value, sub, grand, testId }) {
    if (grand) {
        return (
            <div
                className="flex justify-between items-baseline pt-2 mt-2 border-t-2 border-slate-900 text-[17px] font-semibold font-heading"
                data-testid={testId}
            >
                <span>{label}</span>
                <span className="font-mono text-[#1BA5D9]">{value}</span>
            </div>
        );
    }
    return (
        <div className={"flex justify-between text-[13px] " + (sub ? "text-slate-500" : "text-slate-900")}>
            <span>{label}</span>
            <span className="font-mono">{value}</span>
        </div>
    );
}
function InternalPanel({ state, isCom, com, eqSub, consAnu, finInteres, finFinanciado, alqTotal, alqMargenFin, plazoAnios }) {
    const S = state;
    if (isCom) {
        const eqCosto = equiposCosto(S);
        const consCostoContrato = com.consAnnualCost * com.years;
        const ingreso = com.packageTotal;
        const costo = eqCosto + consCostoContrato;
        const margen = ingreso - costo;
        const margenPct = ingreso > 0 ? (margen / ingreso) * 100 : 0;
        return (
            <div className="border-t border-dashed border-[#C0552B] mt-2 px-5 py-3 bg-orange-50/60 text-[11.5px]" data-testid="internal-panel">
                <div className="text-[10.5px] font-bold uppercase tracking-widest text-[#C0552B] mb-1.5 flex items-center gap-1.5">
                    ● Vista interna comodato — no se imprime
                </div>
                <IRow label="Costo cargado equipo" value={money0(eqCosto)} />
                <IRow label="Equipo + financiamiento recuperado" value={money0(com.equipTotal)} />
                <IRow label="Interés recuperado" value={money0(com.equipTotal - com.valor)} />
                <IRow spaced label={`Costo consumibles (${com.years} a)`} value={money0(consCostoContrato)} />
                <IRow label={`Venta consumibles (${com.years} a)`} value={money0(com.consTotal)} />
                <IRow spaced label="Ingreso total contrato" value={money0(ingreso)} />
                <IRow label="Costo total contrato" value={money0(costo)} />
                <IRow label="Margen del contrato" value={<>{money0(margen)} <Pill>{margenPct.toFixed(0)}%</Pill></>} />
            </div>
        );
    }
    const P = S.settings.params;
    const eqCosto = equiposCosto(S);
    const eqOtros = selEquipos(S).reduce((s, e) => s + (e.fam === "servicio" ? 0 : equipoOtros(S, e) * e.qty), 0);
    const eqMargen = eqSub - eqCosto - eqOtros;
    const eqMargenPct = eqSub > 0 ? (eqMargen / eqSub) * 100 : 0;
    const coCosto = consAnnualCost(S);
    const coMargen = consAnu - coCosto;
    const coMargenPct = consAnu > 0 ? (coMargen / consAnu) * 100 : 0;

    let finBlock = null;
    if (S.mode === "financiamiento") {
        const margenTotal = eqMargen + finInteres;
        finBlock = (
            <>
                <IRow spaced label="Monto financiado" value={money0(finFinanciado)} />
                <IRow label={`Interés ganado (tasa ${(P.tasaFin * 100).toFixed(1)}%)`} value={money0(finInteres)} />
                <IRow
                    label="Margen total (venta + interés)"
                    value={<>{money0(margenTotal)} <Pill>{eqSub > 0 ? (margenTotal / (eqSub + finInteres) * 100).toFixed(0) : 0}%</Pill></>}
                />
            </>
        );
    } else if (S.mode === "alquiler") {
        const margenTotal = eqMargen + alqMargenFin;
        finBlock = (
            <>
                <IRow spaced label={`Total cánones (${S.alq.anios * 12} m)`} value={money0(alqTotal)} />
                <IRow label={`Margen financiero (tasa ${(P.tasaAlq * 100).toFixed(1)}%)`} value={money0(alqMargenFin)} />
                <IRow
                    label="Margen total alquiler"
                    value={<>{money0(margenTotal)} <Pill>{alqTotal > 0 ? (margenTotal / alqTotal * 100).toFixed(0) : 0}%</Pill></>}
                />
            </>
        );
    }

    return (
        <div className="border-t border-dashed border-[#C0552B] mt-2 px-5 py-3 bg-orange-50/60 text-[11.5px]" data-testid="internal-panel">
            <div className="text-[10.5px] font-bold uppercase tracking-widest text-[#C0552B] mb-1.5 flex items-center gap-1.5">
                ● Vista interna — no se imprime
            </div>
            <IRow label="Costo cargado equipos" value={money0(eqCosto)} />
            <IRow label="Otros costos equipos" value={money0(eqOtros)} />
            <IRow label="Precio final equipos (Monto Total)" value={money0(eqSub)} />
            <IRow label="Margen equipos" value={<>{money0(eqMargen)} <Pill>{eqMargenPct.toFixed(0)}%</Pill></>} />
            {finBlock}
            <IRow spaced label="Costo consumibles / año" value={money0(coCosto)} />
            <IRow label="Venta consumibles / año" value={money0(consAnu)} />
            <IRow label="Margen consumibles / año" value={<>{money0(coMargen)} <Pill>{coMargenPct.toFixed(0)}%</Pill></>} />
            {plazoAnios > 1 && <IRow label={`Margen consumibles contrato (${plazoAnios} a)`} value={money0(coMargen * plazoAnios)} />}
        </div>
    );
}
function IRow({ label, value, spaced }) {
    return (
        <div className={"flex justify-between " + (spaced ? "mt-1.5" : "")}>
            <span>{label}</span>
            <span className="font-mono font-semibold">{value}</span>
        </div>
    );
}
function Pill({ children }) {
    return (
        <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full bg-white border border-orange-200 ml-1 text-slate-900">
            {children}
        </span>
    );
}

export default QuotePanel;
